import fetchIntercept from 'fetch-intercept';
import { matchPath } from 'react-router-dom';
import Environment from '@eatzy/common-reactjs/Environment';
import { EXPIRED_TOKEN } from '../../constants/auth';
import { UNAUTHORIZED, FORBIDDEN, NOT_FOUND } from '../../constants/http';
import { logoutRequest } from '../../redux/ducks/UserDucks';
import Authentication from '../../utils/authentication';
import { api } from '../Api';
import { getConfig } from '../../config';
import { isTabletPairingUrl } from '../../utils/tabletPairingPrivacy';

export const authorizationTokenRoutes = [
  { method: 'post', endpoint: '/account/authorize' },
  { method: 'post', endpoint: '/account/refresh' },
  { method: 'post', endpoint: '/account/deauthorize' },
];

// Same-origin guard. fetch-intercept attaches itself to window.fetch globally,
// so every fetch call in the app — including cross-origin Auth Hub calls — flows
// through these interceptors. This interceptor is only meant to handle Eatzy API
// calls (token injection, 403 retry, EXPIRED_TOKEN logout). Cross-origin
// requests must pass through untouched, otherwise the Auth Hub login request
// gets a missing-token 401 from insertAccessTokenInRequest and never reaches
// the network. The Eatzy API origin is read lazily so Environment.parseInitialEnv()
// (called just before registerInterceptors in App.js) has time to populate it.
// Cached only on success — a null result is recomputed on every call so a
// transient misconfiguration (env not yet parsed, malformed URL) doesn't get
// frozen in for the lifetime of the page.
let _eatzyApiOrigin = null;
let _warnedMissingApiOrigin = false;
const getEatzyApiOrigin = () => {
  if (_eatzyApiOrigin) return _eatzyApiOrigin;
  const apiUrl = Environment.getEnv('API_URL') || process.env.REACT_APP_API_URL || '';
  try {
    _eatzyApiOrigin = new URL(apiUrl).origin;
  } catch (_) {
    if (!_warnedMissingApiOrigin) {
      _warnedMissingApiOrigin = true;
      // eslint-disable-next-line no-console
      console.warn(
        '[AccountServiceInterceptors] API_URL is missing or malformed; ' +
          'cross-origin guard cannot identify Eatzy API requests.'
      );
    }
    _eatzyApiOrigin = null;
  }
  return _eatzyApiOrigin;
};

const getRequestOrigin = (url) => {
  try {
    if (!url) return null;
    if (typeof url === 'string') return new URL(url).origin;
    if (url.origin) return url.origin;
    if (url.url) return new URL(url.url).origin;
    return null;
  } catch (_) {
    return null;
  }
};

// Returns true only when we can positively confirm the request targets the
// Eatzy API. When the request origin is known but the Eatzy origin is not, we
// bypass the interceptor — that's the safer choice: a request we can't
// classify shouldn't get a Bearer token injected and shouldn't trigger an
// EXPIRED_TOKEN logout. Same-origin Eatzy calls built via Api.buildConfig
// produce a URL with a valid origin, so they remain on the intercepted path.
const isEatzyApiOrigin = (url) => {
  const requestOrigin = getRequestOrigin(url);
  if (!requestOrigin) return true; // unparseable — preserve existing behavior
  const eatzyOrigin = getEatzyApiOrigin();
  if (!eatzyOrigin) return false; // can't classify → bypass (fail-safe)
  return requestOrigin === eatzyOrigin;
};

const matchesRoute = ({ url, method }, endpoints) => {
  const matchesMethod = endpoints.some(
    (route) => route.method.toLowerCase() === method.toLowerCase()
  );

  const matchesURL = endpoints.some(
    (route) =>
      matchPath(url.pathname, {
        path: route.endpoint,
        exact: true,
        strict: false,
      })
    // eslint-disable-next-line function-paren-newline
  );

  return !!(matchesMethod && matchesURL);
};

export const insertBasicHeaders = async (url, config) => {
  // Don't set Content-Type for multipart requests (FormData)
  // Let the browser set it automatically with the correct boundary
  const isMultipartRequest = config.body instanceof FormData;

  config.headers = {
    ...config.headers,
    ...(isMultipartRequest ? {} : { 'Content-Type': 'application/json' }),
    'X-App-Version': process.env.REACT_APP_VERSION,
    'X-App-Source': 'restaurant-manager-web',
    'X-App-OS':
      window?.navigator?.platform ||
      window?.navigator?.userAgentData?.platform ||
      'unknown',
  };

  return [url, config];
};

export const insertUseRandomFakeData = async (url, config) => {
  const useFakeData = Environment.getEnv('API_FAKE_MODE');

  if (useFakeData === 'true' && config?.headers) {
    config.headers = {
      ...config.headers,
      'X-API-FakeData': 'random',
    };
  }

  return [url, config];
};

export const insertAuthorizationTokenInRequest = async (url, config) => {
  if (config.hasRefreshToken) {
    config.headers = {
      ...config.headers,
      authorization: `Basic ${config.refreshToken}`,
    };
  }

  return [url, config];
};

export const insertAccessTokenInRequest = async (
  url,
  config,
  store,
  isBackEndTokenExpired = false
) => {
  const pairing = isTabletPairingUrl(url);
  const authToken = pairing
    ? await Authentication.getHumanManagementToken()
    : await Authentication.getToken(isBackEndTokenExpired);

  if (!authToken || authToken?.isExpired) {
    if (pairing) {
      return Promise.reject({
        status: UNAUTHORIZED,
        error: {
          code: 'HUMAN_AUTHENTICATION_REQUIRED',
          message: 'Sign in with a human account to manage tablets.',
          recovery_action: 'sign_in',
          retryable: false,
        },
      });
    }
    if (store) store.dispatch(logoutRequest(EXPIRED_TOKEN));
    // Returning a rejected Promise (not undefined) so fetch-intercept routes to
    // requestError instead of trying to spread undefined as [url, config], which
    // would throw "undefined is not iterable".
    return Promise.reject({ message: 'Unauthorized', status: UNAUTHORIZED });
  }

  config.headers = {
    ...config.headers,
    authorization: `Bearer ${authToken?.token}`,
  };

  return [url, config];
};

export const repeatRequest = async (url, config, store) => {
  // insertAccessTokenInRequest rejects if there is no valid token; the rejection
  // propagates naturally through the caller's .catch(). The null guard below is a
  // defensive fallback in case the function ever returns undefined instead.
  const req = await insertAccessTokenInRequest(url, config, store, true);
  if (!req) return Promise.reject({ message: 'Unauthorized', status: UNAUTHORIZED });

  return await api.improvedFetch(
    req[0].pathname + req[0].search,
    {
      ...req[1],
      body: req[1]?.body ? JSON.parse(req[1].body) : undefined,
    },
    1
  );
};

// Per-request metadata keyed by URL string. Using a Map instead of a single
// shared object prevents concurrent requests from overwriting each other's data.
const pendingRequests = new Map();

export const interceptors = (store) => ({
  request(url, config) {
    // Cross-origin requests (Auth Hub) bypass this interceptor entirely.
    if (!isEatzyApiOrigin(url)) {
      return [url, config];
    }

    const refreshRelated =
      matchesRoute({ url, method: config.method }, authorizationTokenRoutes) ||
      config?.hasRefreshToken;

    insertBasicHeaders(url, config);
    if (!isTabletPairingUrl(url)) insertUseRandomFakeData(url, config);

    // Store a snapshot of this request's metadata keyed by its URL so the
    // response handler can retrieve it without being corrupted by a concurrent
    // request overwriting shared state.
    if (!isTabletPairingUrl(url)) pendingRequests.set(url.toString(), {
      url,
      config: { ...config },
      refreshRelated,
    });

    if (refreshRelated) {
      return insertAuthorizationTokenInRequest(url, config, store);
    }
    return insertAccessTokenInRequest(url, config, store);
  },

  requestError(error) {
    // Called when an error occured during another 'request' interceptor call
    return Promise.reject(error);
  },

  async response(response) {
    // Cross-origin responses (Auth Hub) bypass this interceptor — we must not
    // consume their body via response.json() here, otherwise AuthHubService
    // can no longer read it.
    if (!isEatzyApiOrigin(response.url)) {
      return response;
    }

    // Pairing uses the complete shared envelope, including on 401/403/404.
    // Do not enter legacy token-refresh, logout, retry, or metadata caching.
    if (isTabletPairingUrl(response.url)) {
      if (response.status === 204) return null;
      let body;
      try { body = await response.json(); } catch (_) { body = null; }
      if (response.ok) return body;
      return Promise.reject({
        status: response.status,
        error: body?.error,
        responseHeaders: {
          'x-request-id': response.headers?.get?.('X-Request-ID'),
          'retry-after': response.headers?.get?.('Retry-After'),
        },
      });
    }

    // Safely parse the response body — non-JSON responses (e.g. streaming or
    // HTML error pages) must not crash the entire interceptor chain.
    let data;
    try {
      data = await response.json();
    } catch (_) {
      data = null;
    }

    // Retrieve and clean up the per-request metadata for this response.
    const requestData = pendingRequests.get(response.url);
    pendingRequests.delete(response.url);

    // Capture `X-Request-Id` from the response headers BEFORE we hand control
    // back to the application. Once `response.json()` has been consumed and
    // the rejection payload is on its way to Api.js, the original `Response`
    // object is no longer reachable — so the header has to ride along on the
    // rejected payload as `responseHeaders`. This is the gateway-authoritative
    // value, which Api.js prefers over both body fields and the SPA-sent id.
    const headerRequestId =
      (response.headers && typeof response.headers.get === 'function'
        ? response.headers.get('X-Request-Id') ||
          response.headers.get('x-request-id')
        : null) || null;
    const responseHeaders = headerRequestId
      ? { 'x-request-id': headerRequestId }
      : undefined;

    // Always include the HTTP status on every rejection so downstream
    // (Api.js → Bugsnag) can report it. Without this, generic 4xx/5xx
    // rejections lost their status and showed up in Bugsnag with status:
    // undefined.
    const rejectWithStatus = (reject, payload) => {
      const base =
        payload && typeof payload === 'object' && !Array.isArray(payload)
          ? { ...payload }
          : { body: payload };
      if (base.status == null) base.status = response.status;
      if (responseHeaders && !base.responseHeaders) {
        base.responseHeaders = responseHeaders;
      }
      reject(base);
    };

    return new Promise(async (resolve, reject) => {
      if (response.status === UNAUTHORIZED) {
        rejectWithStatus(reject, { message: 'Unauthorized' });
        return;
      }

      if (response.status === FORBIDDEN) {
        // Read-replica lag immediately after login is now handled in
        // UserSaga.signIn via a bounded retry/backoff around the profile
        // fetch, so the interceptor no longer needs a global "fresh login"
        // flag or a hardcoded delay here.
        rejectWithStatus(reject, { message: data?.message });
        return;
      }

      if (
        response.status === NOT_FOUND &&
        data?.message === getConfig('TOKEN_ERROR_MESSAGE')
      ) {
        const { url, config, refreshRelated } = requestData || {};

        if (refreshRelated || !url) {
          rejectWithStatus(reject, data);
          return;
        }

        await repeatRequest(url, config, store)
          .then((resp) => resolve(resp))
          .catch((err) => reject(err));
        return;
      }

      if (response.ok) {
        resolve(data);
      } else {
        rejectWithStatus(reject, data);
      }
    });
  },

  responseError(error) {
    // Handle an fetch error
    return Promise.reject(error);
  },
});

export default (store) => fetchIntercept.register(interceptors(store));
